import { Plus, X } from "lucide-react";
import * as React from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogForm,
  DialogHeader,
  DialogTitle,
  DiscardGuard,
} from "@/components/ui/dialog";
import { Callout } from "@/components/ui/feedback";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/misc";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/components/ui/toast";
import { useSlackConnections, useSlackChannels } from "@/lib/slack";
import { Link } from "react-router-dom";
import { ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { sameValues } from "@/lib/form";
import { useCreateWebhookEndpoint, useTenants, useUpdateWebhookEndpoint } from "@/lib/queries";
import type { WebhookEndpoint, WebhookEndpointCreated } from "@/lib/types";
import {
  NEW_ENDPOINT,
  WEBHOOK_METHODS,
  endpointChanges,
  endpointPayload,
  endpointValues,
  headersProblem,
  urlProblem,
  type EndpointValues,
  type HeaderRow,
} from "@/lib/webhooks";

/**
 * Make a webhook endpoint, or edit one (ADR-022): where the platform sends events, how, and what
 * goes with them.
 *
 * The URL is checked twice. Here for its shape, so a typo does not cost a round trip; and by
 * core-api's SSRF guard when it is saved, which refuses a host on a private network and says why.
 * The signing secret is not on the form. core-api makes it with the endpoint and answers it once,
 * which the page shows; after that it is only rotated.
 */
export function WebhookEndpointFormDialog({
  open,
  onOpenChange,
  endpoint,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Absent for create. */
  endpoint?: WebhookEndpoint;
  /** After a create: the endpoint with its secret, which is shown this once. */
  onCreated?: (created: WebhookEndpointCreated) => void;
}) {
  const toast = useToast();
  const { user: actor } = useAuth();
  const create = useCreateWebhookEndpoint();
  const update = useUpdateWebhookEndpoint();
  // Only a superadmin names a tenant, and only while there are several: with one, core-api fills
  // it in, and everyone else's is their own whatever they send.
  const isSuperadmin = actor?.role === "superadmin";
  const { data: tenants } = useTenants({
    enabled: open && isSuperadmin && !endpoint,
  });
  const pickTenant = isSuperadmin && !endpoint && (tenants?.length ?? 0) > 1;

  const [values, setValues] = React.useState<EndpointValues>(NEW_ENDPOINT);
  // What the form was loaded with, which an edit is measured against.
  const [loaded, setLoaded] = React.useState<EndpointValues>(NEW_ENDPOINT);
  const [tenantId, setTenantId] = React.useState<number | null>(null);
  const [kind, setKind] = React.useState<"http" | "slack">("http");
  const [connectionId, setConnectionId] = React.useState<number | null>(null);
  const [channelId, setChannelId] = React.useState("");
  const connections = useSlackConnections(open);
  const channels = useSlackChannels(connectionId, open && kind === "slack");
  const availableConnections = (connections.data ?? []).filter(
    (c) =>
      (c.enabled || c.id === endpoint?.slack_connection_id) &&
      (endpoint ? c.tenant_id === endpoint.tenant_id : tenantId ? c.tenant_id === tenantId : true),
  );
  const [touched, setTouched] = React.useState(false);
  const [formError, setFormError] = React.useState<string | null>(null);

  // Loaded when it opens, and for another endpoint, but not each time the same one is fetched
  // again while somebody edits, which wiped what they had typed.
  const latest = React.useRef(endpoint);
  latest.current = endpoint;
  React.useEffect(() => {
    if (!open) return;
    const start = latest.current ? endpointValues(latest.current) : NEW_ENDPOINT;
    setValues(start);
    setLoaded(start);
    setTenantId(null);
    setKind(latest.current?.kind ?? "http");
    setConnectionId(latest.current?.slack_connection_id ?? null);
    setChannelId(latest.current?.slack_channel_id ?? "");
    setTouched(false);
    setFormError(null);
  }, [open, endpoint?.id]);

  const set = <K extends keyof EndpointValues>(key: K, value: EndpointValues[K]) =>
    setValues((current) => ({ ...current, [key]: value }));
  const setHeader = (index: number, row: HeaderRow) =>
    set(
      "headers",
      values.headers.map((header, i) => (i === index ? row : header)),
    );

  const nameProblem = values.name.trim() ? null : "Required";
  const urlError =
    kind === "http"
      ? urlProblem(values.url)
      : !connectionId || !channelId
        ? "Choose a workspace and channel"
        : null;
  const headersError = headersProblem(values.headers);
  const changed =
    !sameValues(values, loaded) ||
    tenantId !== null ||
    kind !== (endpoint?.kind ?? "http") ||
    connectionId !== (endpoint?.slack_connection_id ?? null) ||
    channelId !== (endpoint?.slack_channel_id ?? "");

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setTouched(true);
    setFormError(null);
    if (nameProblem || urlError || headersError) return;
    if (pickTenant && tenantId === null) {
      setFormError("Choose the tenant whose events it hears.");
      return;
    }
    try {
      if (endpoint) {
        const changes =
          kind === "http"
            ? endpointChanges(values, endpoint)
            : {
                name: values.name.trim(),
                enabled: values.enabled,
                slack_connection_id: connectionId,
                slack_channel_id: channelId,
                slack_channel_name:
                  channels.data?.find((c) => c.id === channelId)?.name ?? endpoint.slack_channel_name,
              };
        if (Object.keys(changes).length > 0) await update.mutateAsync({ id: endpoint.id, ...changes });
        toast.success("Endpoint saved", values.name.trim());
        onOpenChange(false);
      } else {
        const created = await create.mutateAsync({
          ...(kind === "http"
            ? endpointPayload(values)
            : {
                kind: "slack" as const,
                name: values.name.trim(),
                slack_connection_id: connectionId,
                slack_channel_id: channelId,
                slack_channel_name: channels.data?.find((c) => c.id === channelId)?.name,
              }),
          ...(pickTenant && tenantId !== null ? { tenant_id: tenantId } : {}),
        });
        onOpenChange(false);
        onCreated?.(created);
      }
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Could not save the endpoint.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="drawer" size="lg">
        <DiscardGuard changed={changed} />
        <DialogHeader>
          <DialogTitle>{endpoint ? "Edit endpoint" : "New endpoint"}</DialogTitle>
          <DialogDescription>
            {endpoint
              ? "Where its events go, and what goes with them."
              : "Send events to an HTTP destination or a Slack channel."}
          </DialogDescription>
        </DialogHeader>

        <DialogForm onSubmit={onSubmit} noValidate>
          <DialogBody className="space-y-4">
            {formError && <Callout tone="danger">{formError}</Callout>}

            <Field
              label="Name"
              htmlFor="endpoint-name"
              required
              error={touched ? (nameProblem ?? undefined) : undefined}
              hint="How this console lists it. Unique among your endpoints."
            >
              <Input
                autoComplete="off"
                maxLength={200}
                placeholder="Zapier: signed cases"
                invalid={touched && Boolean(nameProblem)}
                value={values.name}
                onChange={(e) => set("name", e.target.value)}
              />
            </Field>

            {pickTenant && (
              <Field label="Tenant" htmlFor="endpoint-tenant" required hint="Whose events it hears.">
                <Select
                  value={tenantId?.toString() ?? ""}
                  onValueChange={(next) => {
                    setTenantId(Number(next));
                    setConnectionId(null);
                    setChannelId("");
                  }}
                >
                  <SelectTrigger id="endpoint-tenant">
                    <SelectValue placeholder="Choose a tenant" />
                  </SelectTrigger>
                  <SelectContent>
                    {(tenants ?? []).map((tenant) => (
                      <SelectItem key={tenant.id} value={String(tenant.id)}>
                        {tenant.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>
            )}

            {!endpoint && (
              <Field label="Type" htmlFor="endpoint-kind">
                <Select value={kind} onValueChange={(v) => setKind(v as "http" | "slack")}>
                  <SelectTrigger id="endpoint-kind">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="http">HTTP</SelectItem>
                    <SelectItem value="slack">Slack</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
            )}
            {kind === "slack" && (
              <>
                <Field label="Workspace" htmlFor="endpoint-workspace" required>
                  <Select
                    value={connectionId ? String(connectionId) : ""}
                    onValueChange={(v) => {
                      setConnectionId(Number(v));
                      setChannelId("");
                    }}
                  >
                    <SelectTrigger id="endpoint-workspace">
                      <SelectValue placeholder="Choose workspace" />
                    </SelectTrigger>
                    <SelectContent>
                      {availableConnections.map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>
                          {c.name} · {c.team_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Link to="/slack" className="text-xs underline">
                  Connect a workspace
                </Link>
                {connections.error && <Callout tone="danger">{connections.error.message}</Callout>}
                {channels.error && (
                  <Callout tone="danger">
                    {channels.error.message}
                    <Button type="button" variant="ghost" onClick={() => void channels.refetch()}>
                      Try again
                    </Button>
                  </Callout>
                )}
                <Field
                  label="Channel"
                  htmlFor="endpoint-channel"
                  required
                  hint="Invite the app to a private channel first."
                  error={touched ? (urlError ?? undefined) : undefined}
                >
                  <Select value={channelId} onValueChange={setChannelId}>
                    <SelectTrigger id="endpoint-channel">
                      <SelectValue
                        placeholder={channels.isFetching ? "Loading channels…" : "Choose channel"}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {channels.data?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          #{c.name}
                          {c.is_private ? " (private)" : ""}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
              </>
            )}
            {kind === "http" && (
              <>
                <Field
                  label="URL"
                  htmlFor="endpoint-url"
                  required
                  error={touched ? (urlError ?? undefined) : undefined}
                  hint="For Zapier: start a zap with Webhooks by Zapier, choose Catch Hook, and paste the URL it gives you here. The URL has to be reachable from the internet."
                >
                  <Input
                    autoComplete="off"
                    className="font-mono"
                    placeholder="https://hooks.zapier.com/hooks/catch/…"
                    invalid={touched && Boolean(urlError)}
                    value={values.url}
                    onChange={(e) => set("url", e.target.value)}
                  />
                </Field>

                <Field
                  label="Method"
                  htmlFor="endpoint-method"
                  hint="POST, unless the receiver asks for another."
                >
                  <Select value={values.method} onValueChange={(next) => set("method", next)}>
                    <SelectTrigger id="endpoint-method" className="w-32">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {WEBHOOK_METHODS.map((method) => (
                        <SelectItem key={method} value={method}>
                          {method}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <div className="space-y-2">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-medium text-text">Headers</p>
                      <p className="text-xs text-text-muted">
                        Sent with every delivery: a receiver's own API key, say. Every admin can read them
                        here.
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => set("headers", [...values.headers, { name: "", value: "" }])}
                    >
                      <Plus />
                      Add a header
                    </Button>
                  </div>
                  {values.headers.map((row, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <Input
                        aria-label={`Header ${index + 1}, name`}
                        autoComplete="off"
                        className="w-48 font-mono"
                        placeholder="Authorization"
                        value={row.name}
                        onChange={(e) => setHeader(index, { ...row, name: e.target.value })}
                      />
                      <Input
                        aria-label={`Header ${index + 1}, value`}
                        autoComplete="off"
                        className="min-w-0 flex-1 font-mono"
                        placeholder="Bearer …"
                        value={row.value}
                        onChange={(e) => setHeader(index, { ...row, value: e.target.value })}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        aria-label={`Remove header ${index + 1}`}
                        onClick={() =>
                          set(
                            "headers",
                            values.headers.filter((_, i) => i !== index),
                          )
                        }
                      >
                        <X />
                      </Button>
                    </div>
                  ))}
                  {touched && headersError && (
                    <p role="alert" className="text-xs text-danger-11">
                      {headersError}
                    </p>
                  )}
                </div>
              </>
            )}
            {endpoint && (
              <div className="flex items-center justify-between gap-4 rounded-md border border-border px-3 py-2.5">
                <div>
                  <p id="endpoint-enabled-label" className="text-xs font-medium text-text">
                    Sends events
                  </p>
                  <p className="text-xs text-text-muted">
                    Off, it keeps its events and its log and sends nothing. A delivery queued while it is off
                    fails, and can be sent again once it is back on.
                  </p>
                </div>
                <Switch
                  checked={values.enabled}
                  onCheckedChange={(checked) => set("enabled", checked)}
                  aria-labelledby="endpoint-enabled-label"
                />
              </div>
            )}
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="default" loading={create.isPending || update.isPending}>
              {endpoint ? "Save changes" : "Add endpoint"}
            </Button>
          </DialogFooter>
        </DialogForm>
      </DialogContent>
    </Dialog>
  );
}
